export interface HeroCategory {
    label: string
    apps: Array<string>
}

export const heroCategories: Array<HeroCategory> = [
    { label: "Marketing", apps: ["Klaviyo", "Mailchimp", "HubSpot", "Google Ads", "Meta Ads", "ActiveCampaign", "SendGrid"] },
    { label: "Ecommerce", apps: ["Shopify", "BigCommerce", "WooCommerce", "Amazon Seller Central", "Walmart Marketplace", "Etsy", "Adobe Commerce"] },
    { label: "Finance", apps: ["QuickBooks Online", "NetSuite", "Xero", "Stripe", "Bill.com", "Sage Intacct", "Ramp"] },
    { label: "Logistics", apps: ["ShipStation", "ShipBob", "Shippo", "Flexport", "FedEx", "UPS", "Extensiv"] },
    { label: "Healthcare", apps: ["athenahealth", "DrChrono", "Tebra", "Elation Health", "Zocdoc", "Spruce Health", "Salesforce"] },
    { label: "Field service", apps: ["ServiceTitan", "Jobber", "Housecall Pro", "FieldEdge", "Aspire", "CompanyCam", "BuildOps"] }
]

export const storyParagraphs = [
    "Integrations are complex, and mission-critical workflows demand deep knowledge of your business. Most competitors hand you a platform and pass you to a third party.",
    "We have seen that fail, because integrations break once the third party has left.",
    "So FlowCraft keeps the whole crew in-house. Solutions Engineers, PMs, Integration Engineers and Support scope, build, launch and maintain every workflow with you."
]

export interface StageStep {
    title: string
    description: string
}

export interface Stage {
    id: string
    label: string
    intro: string
    emphasis: string
    person: {
        name: string
        role: string
        image: string
    }
    steps: Array<StageStep>
}

export const stages: Array<Stage> = [
    {
        id: 'requirements',
        label: "Requirements",
        intro: "The costliest part of an enterprise workflow is the question nobody asked.",
        emphasis: " We know what to ask so no edge case slips through.",
        person: {
            name: "Marcus Dellinger",
            role: "Solutions Engineer",
            image: '/images/people/marcus-dellinger.jpg'
        },
        steps: [
            { title: "Workflow inventory", description: "We list every workflow you run today, the systems at each end, and the volume each one carries, so nothing is priced from a guess." },
            { title: "Requirements blueprint", description: "A written blueprint per workflow covering trigger, field mapping, error handling and every edge case we found." },
            { title: "Credentials and access", description: "We work with you to obtain access to every system the integration touches." },
            { title: "Scope sign-off", description: "Nothing moves into development until you approve the blueprint. It becomes the standard we measure the final build against." }
        ]
    },
    {
        id: 'build',
        label: "Build",
        intro: "Our dedicated integration engineers build your workflow on Studio,",
        emphasis: " our next-generation integration platform.",
        person: {
            name: "Ingrid Solheim",
            role: "Integration Engineer",
            image: '/images/people/ingrid-solheim.jpg'
        },
        steps: [
            { title: "Workflow buildout", description: "One engineer builds and owns your workflow end to end, so you always know who to ask." },
            { title: "Internal testing", description: "Your real data goes through, then every edge case from the blueprint, until it runs with nobody in the loop." },
            { title: "User acceptance testing", description: "You test against your own process with our engineer beside you. Anything you flag is fixed first." },
            { title: "Client sign-off", description: "You confirm in writing that it does what the blueprint promised." },
            { title: "Monitored go-live", description: "We switch it on and watch the first runs with you, because day one in production gets our closest attention." }
        ]
    },
    {
        id: 'launch',
        label: "Launch",
        intro: "Your workflow is live, and this is only the beginning.",
        emphasis: " We monitor it and keep every connection current and error-free.",
        person: {
            name: "Tomasz Varga",
            role: "Head of Customer Success",
            image: '/images/people/tomasz-varga.jpg'
        },
        steps: [
            { title: "Error monitoring", description: "We watch every run for failures and step in the moment something needs attention, often before your team notices." },
            { title: "Connection monitoring", description: "Credentials expire and vendors change their APIs without warning. We keep every connection fresh so a workflow never goes quiet." },
            { title: "Client check-ins", description: "Your team meets ours on a regular call, and we adapt workflows to your business before problems appear." },
            { title: "Workflow optimization", description: "We review run times and volumes as you grow and tune accordingly." },
            { title: "Dedicated support hours", description: "Your plan includes time with a support team that understands your workflows." }
        ]
    }
]

export interface Workflow {
    title: string
    description: string
    from: string
    to: string
}

export const showcaseApps = [
    "NetSuite",
    "Shopify",
    "QuickBooks Online",
    "Salesforce",
    "HubSpot",
    "WooCommerce",
    "BigCommerce",
    "Stripe",
    "Slack",
    "ShipStation",
    "Zendesk",
    "Xero"
]

export const showcaseQueryAliases: Record<string, string> = {
    netsuite: "NetSuite",
    shopify: "Shopify",
    quickbooks: "QuickBooks Online",
    salesforce: "Salesforce",
    hubspot: "HubSpot",
    woocommerce: "WooCommerce",
    bigcommerce: "BigCommerce",
    stripe: "Stripe",
    slack: "Slack",
    shipstation: "ShipStation",
    zendesk: "Zendesk",
    xero: "Xero"
}

// Hand-written examples for the most requested pair. Every other pair is assembled from each app's objects.
export const netsuiteShopifyWorkflows: Array<Workflow> = [
    { title: "Inventory", description: "Update Shopify inventory levels from NetSuite stock quantities", from: "NetSuite", to: "Shopify" },
    { title: "Item Fulfillments", description: "Update Shopify shipments from NetSuite fulfillments with tracking", from: "NetSuite", to: "Shopify" },
    { title: "Orders", description: "Create NetSuite sales orders from Shopify orders", from: "Shopify", to: "NetSuite" },
    { title: "Refunds", description: "Create NetSuite refunds from Shopify refunded orders", from: "Shopify", to: "NetSuite" },
    { title: "Shipments", description: "Create Shopify fulfillments from NetSuite item shipments", from: "NetSuite", to: "Shopify" },
    { title: "Return Authorizations", description: "Create NetSuite return authorizations from Shopify approved returns", from: "Shopify", to: "NetSuite" },
    { title: "Cash Sales", description: "Create NetSuite cash sales from Shopify order transactions", from: "Shopify", to: "NetSuite" },
    { title: "Order Transactions", description: "Create Shopify order transactions from NetSuite cash sales", from: "NetSuite", to: "Shopify" },
    { title: "Refund Data", description: "Create Shopify refunds from NetSuite refund data", from: "NetSuite", to: "Shopify" },
    { title: "Customers", description: "Create NetSuite customers from new Shopify customers", from: "Shopify", to: "NetSuite" },
    { title: "Customer Updates", description: "Update Shopify customer profiles from NetSuite customer records", from: "NetSuite", to: "Shopify" },
    { title: "Products", description: "Create Shopify products from NetSuite inventory items", from: "NetSuite", to: "Shopify" },
    { title: "Pricing", description: "Update Shopify prices from NetSuite price levels", from: "NetSuite", to: "Shopify" },
    { title: "Product Images", description: "Sync NetSuite item images to Shopify product media", from: "NetSuite", to: "Shopify" },
    { title: "Locations", description: "Map NetSuite locations to Shopify inventory locations", from: "NetSuite", to: "Shopify" },
    { title: "Payouts", description: "Create NetSuite deposits from Shopify payouts", from: "Shopify", to: "NetSuite" },
    { title: "Gift Cards", description: "Create NetSuite gift certificates from Shopify gift card sales", from: "Shopify", to: "NetSuite" },
    { title: "Discounts", description: "Apply Shopify discount codes to NetSuite sales order lines", from: "Shopify", to: "NetSuite" },
    { title: "Invoices", description: "Create NetSuite invoices from Shopify B2B orders", from: "Shopify", to: "NetSuite" },
    { title: "Credit Memos", description: "Create NetSuite credit memos from Shopify partial refunds", from: "Shopify", to: "NetSuite" },
    { title: "Order Cancellations", description: "Close NetSuite sales orders from cancelled Shopify orders", from: "Shopify", to: "NetSuite" },
    { title: "Order Status", description: "Update Shopify order tags from NetSuite order status", from: "NetSuite", to: "Shopify" },
    { title: "B2B Companies", description: "Create Shopify B2B companies from NetSuite customer accounts", from: "NetSuite", to: "Shopify" },
    { title: "Payment Terms", description: "Update Shopify company payment terms from NetSuite terms", from: "NetSuite", to: "Shopify" },
    { title: "Taxes", description: "Map Shopify tax lines to NetSuite tax codes on sales orders", from: "Shopify", to: "NetSuite" },
    { title: "Kits and Bundles", description: "Update Shopify bundle availability from NetSuite kit items", from: "NetSuite", to: "Shopify" },
    { title: "Backorders", description: "Flag Shopify orders when NetSuite lines go on backorder", from: "NetSuite", to: "Shopify" }
]

export interface CaseStudy {
    company: string
    industry: string
    problems: Array<string>
    location: string
    since: string
    website: string
    quote?: string
    person?: string
    title?: string
    metrics?: Array<{ label: string, value: string }>
    story: {
        challenge: string
        solution: string
        results: Array<string>
    }
}

export const caseStudies: Array<CaseStudy> = [
    {
        company: "Wexford Rail Models",
        industry: "Model railways and collectibles",
        problems: ["Shopify to NetSuite integration", "Two marketing data feeds", "Workflow monitoring and support"],
        location: "Raleigh, North Carolina, USA",
        since: "2025",
        website: "wexfordrail.com",
        quote: "FlowCraft has been essential. The responsiveness, the focus on us, the feeling that it was our project, showed up every single day.",
        person: "Colm Reardon",
        title: "Director of Web Services",
        metrics: [
            { label: "Years in business", value: "90+ years" },
            { label: "Product records updated nightly", value: "11,000+" },
            { label: "Customer records delivered", value: "205k+/wk" }
        ],
        story: {
            challenge: "Wexford sells thousands of model railway products through Shopify, while orders, inventory and customer accounts live in NetSuite. Updates moved by hand, and product data drifted between the two systems.",
            solution: "FlowCraft connected Shopify and NetSuite end to end, built two marketing data feeds on top of the same product records, and took over monitoring and support for every workflow.",
            results: ["11,000+ product records updated every night", "205k+ customer records delivered each week", "One accountable partner for every workflow"]
        }
    },
    {
        company: "Ridgeway",
        industry: "Utility vegetation management",
        problems: ["Salesforce to Sage sync", "NetSuite and payroll integration", "Consolidating acquired systems"],
        location: "Savannah, Georgia, USA",
        since: "2022",
        website: "ridgewayvs.com",
        story: {
            challenge: "A run of acquisitions left Ridgeway with field crews, finance and payroll spread across disconnected systems, and teams re-entering the same data in each one.",
            solution: "We synced Salesforce with Sage, integrated NetSuite with payroll, and folded each acquired company's systems into one set of shared workflows.",
            results: ["Acquired teams onboarded onto shared workflows", "Payroll data posted without re-keying", "Working together since 2022"]
        }
    },
    {
        company: "Sable Coffee",
        industry: "Specialty coffee",
        problems: ["Shopify to QuickBooks sync", "Invoice and order sync", "Accounting reconciliation"],
        location: "Portland, Maine, USA",
        since: "2024",
        website: "sablecoffee.co",
        story: {
            challenge: "Online orders, wholesale invoices and payouts were reconciled by hand at the end of every week, and the books were always a few days behind.",
            solution: "FlowCraft synced Shopify with QuickBooks, kept invoices and orders matched across both, and automated the reconciliation that used to take a day.",
            results: ["Orders and invoices posted automatically", "Reconciliation that runs on its own", "Books that stay current"]
        }
    },
    {
        company: "Helix Parts",
        industry: "Automotive software",
        problems: ["WooCommerce to NetSuite", "Order and refund automation", "Inventory and shipment sync"],
        location: "Naperville, Illinois, USA",
        since: "2025",
        website: "helixparts.com",
        story: {
            challenge: "Helix sells software licenses and hardware through WooCommerce, but orders, refunds and stock were managed in NetSuite with manual imports in between.",
            solution: "We connected WooCommerce to NetSuite, automated orders and refunds in both directions, and kept inventory and shipment updates in sync.",
            results: ["Orders and refunds automated end to end", "Stock levels that match across systems", "Shipment updates sent without manual steps"]
        }
    },
    {
        company: "Kestrel Freight",
        industry: "Third-party logistics",
        problems: ["Customer API integrations", "Ecommerce and ERP connectivity", "Marketplace and warehouse sync"],
        location: "Riverside, California, USA",
        since: "2025",
        website: "kestrelfreight.com",
        story: {
            challenge: "Every new 3PL client arrived with its own ecommerce platform, ERP and marketplaces, and each connection took weeks of custom work.",
            solution: "FlowCraft built reusable customer API integrations, connected client storefronts and ERPs to the warehouse, and kept marketplace orders in sync.",
            results: ["Faster onboarding for every new client", "Marketplace orders flowing straight to the warehouse", "One partner for every client connection"]
        }
    },
    {
        company: "Copperleaf",
        industry: "Outdoor equipment",
        problems: ["BigCommerce to ERP sync", "Returns automation", "EDI feeds"],
        location: "Boulder, Colorado, USA",
        since: "2023",
        website: "copperleafoutdoor.com",
        story: {
            challenge: "Copperleaf sells direct and through retail partners, and orders, returns and EDI documents were all processed by hand.",
            solution: "We synced BigCommerce with their ERP, automated returns from request to refund, and set up EDI feeds for their retail partners.",
            results: ["Returns processed without manual steps", "EDI orders flowing straight into the ERP", "One view of every channel"]
        }
    }
]

export interface FaqItem {
    question: string
    answer: string
}

export const faqs: Array<FaqItem> = [
    {
        question: "What systems can FlowCraft connect?",
        answer: "FlowCraft connects the systems that run your operations: ERP, ecommerce, CRM, accounting, warehouse and shipping, field service, payroll, EDI, marketplaces and more. Our team works every day with platforms such as NetSuite, Shopify, Salesforce, Microsoft Dynamics 365, Acumatica, QuickBooks Online, ServiceTitan, Sage Intacct, ShipStation and Amazon."
    },
    {
        question: "Can FlowCraft connect systems with no prebuilt connector?",
        answer: "Yes. FlowCraft is not limited to a fixed library of connectors. If a system offers a secure way to exchange data, such as an API, a database, a webhook or a file exchange, our engineers can usually build a custom connection for it."
    },
    {
        question: "How is FlowCraft different from other integration platforms?",
        answer: "You get the people and the platform together. Our in-house team scopes, builds, launches, monitors and supports your integrations on our own platform, so you have one accountable partner instead of software you manage alone or hand off to a third party."
    },
    {
        question: "What makes FlowCraft's technology different?",
        answer: "FlowCraft was built AI-first as one unified system. Instead of adding AI on top of an older platform, every part was designed to work together from the start, which gives our team a solid foundation for building integrations that stay reliable."
    },
    {
        question: "How long does a typical project take?",
        answer: "It depends on the systems, the workflows and how complex they are, but many integrations go live in a matter of weeks. During discovery we define the scope and give you a clear plan and timeline before any development begins."
    },
    {
        question: "How much does an integration cost?",
        answer: "Pricing depends on the systems, the workflows, your data volume and the complexity involved. We scope your requirements first and price the work upfront, so you know exactly what to expect before anything moves forward."
    },
    {
        question: "Can FlowCraft handle custom fields and business logic?",
        answer: "Yes. Integrations are shaped around your process, including custom fields, data transformations, routing rules, conditional logic, multi-step workflows and any requirements specific to your systems."
    },
    {
        question: "What support is included?",
        answer: "Full service is an all-inclusive annual subscription, so support is never a separate add-on. It includes dedicated support, a technical SLA and three hours of professional services every month for adjustments, improvements and guidance."
    },
    {
        question: "What happens after go-live?",
        answer: "We stay involved. FlowCraft monitors and supports every integration after launch, troubleshoots issues, makes adjustments and keeps things running as your business changes."
    },
    {
        question: "What if a connected system changes its API?",
        answer: "API and platform changes are a normal part of running integrations. Because support is ongoing, our team makes the updates as your systems evolve, so your people never have to troubleshoot or rebuild anything themselves."
    },
    {
        question: "How do we get started?",
        answer: "Start with a discovery call. We review the systems you want to connect, the data that needs to move between them and your business requirements. From there we recommend an approach, define the scope and set clear next steps."
    }
]
